/**
 * ============================================================================
 * simulatorUiMode — Toggle "Rápido / Avanzado" del simulador pro-forma
 * ============================================================================
 * - Solo afecta visibilidad de UI; NO altera datos ni cálculos.
 * - Persistido en localStorage por usuario (preferencia transversal).
 * - Permite "auto-escalada" a Avanzado cuando el snapshot tiene casos
 *   complejos (CBM, ICO, beneficios aduaneros, etc.).
 * ============================================================================
 */

export type UiMode = "quick" | "advanced";

/** Claves canónicas para columnas/inputs del simulador. */
export type ItemFieldKey =
  | "sku"
  | "moneda"
  | "unidad"
  | "cantidad"
  | "precio_unitario"
  | "peso"
  | "cbm"
  | "grados"
  | "litros"
  | "tarifaIco"
  | "arancel"
  | "iva"
  | "beneficio"
  | "sub_total"
  | "actions"
  // Campos del wizard
  | "hs_code"
  | "ico_group"
  | "regimen";

export const QUICK_VISIBLE_ITEM_COLS: ReadonlySet<ItemFieldKey> = new Set([
  "sku",
  "moneda",
  "cantidad",
  "precio_unitario",
  "sub_total",
  "actions",
  "hs_code",
]);

export const ADVANCED_VISIBLE_ITEM_COLS: ReadonlySet<ItemFieldKey> = new Set([
  "sku",
  "moneda",
  "unidad",
  "cantidad",
  "precio_unitario",
  "peso",
  "cbm",
  "grados",
  "litros",
  "tarifaIco",
  "arancel",
  "iva",
  "beneficio",
  "sub_total",
  "actions",
  "hs_code",
  "ico_group",
  "regimen",
]);

export function isVisible(mode: UiMode, col: ItemFieldKey): boolean {
  return (mode === "quick" ? QUICK_VISIBLE_ITEM_COLS : ADVANCED_VISIBLE_ITEM_COLS).has(col);
}

// ── Persistencia ───────────────────────────────────────────────────────────
const STORAGE_KEY = "simulator-ui-mode";

export function loadUiMode(): UiMode {
  if (typeof window === "undefined") return "quick";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "advanced" ? "advanced" : "quick";
  } catch {
    return "quick";
  }
}

export function saveUiMode(mode: UiMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* noop */
  }
}

// ── Auto-escalada: detecta si el snapshot exige modo Avanzado ─────────────
interface ForceAdvancedItem {
  tarifa_ico?: number | null;
  grados_alcoholicos?: number | null;
  beneficio_aduanero?: string | null;
  aplica_tlc?: boolean | null;
  ico_tipo?: string | null;
}
interface ForceAdvancedGasto {
  base_prorrateo?: string | null;
}

export interface ForceAdvancedReason {
  forced: boolean;
  reasons: string[];
}

export function shouldForceAdvanced(
  items: ForceAdvancedItem[] = [],
  gastos: ForceAdvancedGasto[] = [],
): ForceAdvancedReason {
  const reasons: string[] = [];

  if (gastos.some((g) => (g.base_prorrateo ?? "").toString().toUpperCase() === "CBM")) {
    reasons.push("Hay gastos prorrateados por CBM (volumen).");
  }

  if (items.some((it) => Number(it.tarifa_ico ?? 0) > 0 || Number(it.grados_alcoholicos ?? 0) > 0)) {
    reasons.push("Hay ítems con ICO (bebidas alcohólicas).");
  }

  if (items.some((it) => (it.ico_tipo ?? "").toString().toUpperCase() === "VEHICULOS")) {
    reasons.push("Hay ítems con ICO de vehículos.");
  }

  if (
    items.some((it) => {
      const ben = (it.beneficio_aduanero ?? "GENERAL").toString().toUpperCase();
      return ben && ben !== "GENERAL";
    })
  ) {
    reasons.push("Hay ítems con régimen aduanero distinto a GENERAL.");
  }

  return { forced: reasons.length > 0, reasons };
}
